
import React, { useState, useCallback, useRef } from 'react';
import { Button } from '../../ui/button';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { KYPPageState, KYPPageActions } from '../../../lib/kyp-search/hooks/useKYPPageState';
import { PlayerProfileCard } from '../PlayerProfileCard';
import { PlayerAnalysisCard } from '../PlayerAnalysisCard';
import { EnhancedPlayerSimilaritySection } from '../../EnhancedPlayerSimilaritySection';
import { KYPSearchIndicatorNew } from './KYPSearchIndicatorNew';

import { IndividualSummariesList } from '../IndividualSummariesList';
import { CategoryGrid } from '../category-polling/CategoryGrid';
import { KYPResultsSection } from '../KYPResultsSection';
import { AthleteIntelligenceScore } from '../AthleteIntelligenceScore';

import { ExportReportButton } from '../ExportReportButton';

interface KYPSearchPhaseProps {
  state: KYPPageState;
  actions: KYPPageActions;
}

export const KYPSearchPhase: React.FC<KYPSearchPhaseProps> = ({ state, actions }) => {
  const { 
    phase,
    playerProfile, 
    playerStats, 
    playerInjuries, 
    playerTransfers, 
    playerAchievements,
    searchResults,
    searchStatus,
    selectedPlayer,
    error,
    kypPipelineType,
    isPolling,
  } = state;
  
  const { cancelKYPSearch, clearSearch } = actions;
  
  // Note: Article summaries are now embedded in category data, no separate state needed

  // Ref for scrolling to KYP Analysis Results
  const kypResultsRef = useRef<HTMLDivElement>(null);

  // State for managing selected category from AIS component
  const [selectedCategoryFromAIS, setSelectedCategoryFromAIS] = useState<string | undefined>(undefined);

  // Function to scroll to KYP Analysis Results
  const scrollToAnalysis = useCallback(() => {
    if (kypResultsRef.current) {
      kypResultsRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  }, []);

  // Function to handle category click from AIS component
  const handleCategoryClick = useCallback((categoryName: string) => {
    setSelectedCategoryFromAIS(categoryName);
  }, []);

  // Note: Article summaries loading removed - now embedded in category data

  // Debug logging for state transitions
  console.log('KYPSearchPhase render state:', {
    phase,
    searchStatus,
    hasSearchResults: !!searchResults,
    error,
    selectedPlayer: selectedPlayer?.name
  });
  
  // Determine if search is complete
  const isComplete = phase === 'kyp_complete';
  // Determine if search is cancelled
  const isCancelled = phase === 'kyp_cancelled';
  // Determine if search is in progress
  const isInProgress = phase === 'kyp_running' && searchStatus === 'in_progress';
  // Determine if search is processing results (completed but results being fetched)
  const isProcessingResults = phase === 'kyp_running' && searchStatus === 'completed';
  // Determine if we're in an unknown/transition state that needs handling
  const needsFallbackHandling = phase === 'kyp_running' && 
    !['in_progress', 'completed', 'failed', 'cancelled'].includes(searchStatus || '');
  
  return (
    <div className="space-y-6">

      {/* Loading indicator for KYP search in progress */}
      {(isInProgress || isProcessingResults) && (
        <KYPSearchIndicatorNew state={state} actions={actions} />
      )}
      
      {/* Initial loading indicator for parallel categories when search starts but polling hasn't begun */}
      {(kypPipelineType === 'parallel_categories' || kypPipelineType === 'parallel_categories_mock') &&
       phase === 'kyp_running' && !state.isPolling && state.completedCategories.length === 0 && (
        <div className="bg-[#1a1a3a] rounded-lg p-4 mb-4 border border-[#4E46FF]">
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#4E46FF]"></div>
            <div>
              <span className="font-medium text-[#CECFD2]">
                Initializing KYP Search
              </span>
              <p className="text-sm text-gray-400 mt-1">
                Starting parallel category analysis with real-time updates...
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Fallback loading state for edge cases */}
      {needsFallbackHandling && (
        <div className="bg-[#1a1a3a] rounded-lg p-4 mb-4">
          <div className="flex items-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#4E46FF]"></div>
            <span className="font-medium text-[#CECFD2]">
              Processing search results...
            </span>
          </div>
        </div>
      )}
      


      {/* Player info and actions section */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-[#CECFD2]">
            {selectedPlayer?.name}
          </h2>
          <p className="text-gray-400">
            {playerProfile?.club_name} • {playerProfile?.position}
          </p>
        </div>
        
        <div className="flex gap-2">
          {(isComplete || isCancelled) && (
            <Button
              onClick={clearSearch}
              variant="outline"
              className="border-transparent bg-transparent hover:bg-[#26222E] text-[#CECFD2]"
            >
              Clear
            </Button>
          )}
        </div>
      </div>
      
      {/* ATHLETE INTELLIGENCE SCORE SUMMARY - TOP POSITION */}
      {(kypPipelineType === 'parallel_categories' || kypPipelineType === 'parallel_categories_mock') &&
       state.completedCategories.length > 0 && (
        <AthleteIntelligenceScore
          completedCategories={state.completedCategories}
          onScrollToAnalysis={scrollToAnalysis}
          onCategoryClick={handleCategoryClick}
          className="mb-6"
        />
      )}

      {/* Search status indicators */}
      {isComplete && (
        <div className="bg-[#1a3f1a] rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-400" />
              <span className="font-medium text-[#CECFD2]">
                KYP Search completed. See below for details
              </span>
            </div>
            <div>
              {/* Export KYP Report button */}
              {searchResults && playerProfile && (
                <ExportReportButton
                  playerProfile={playerProfile}
                  playerData={searchResults}
                  playerInjuries={playerInjuries}
                  playerTransfers={playerTransfers}
                  playerAchievements={playerAchievements}
                  playerStats={playerStats}
                  variant="outline"
                  size="sm"
                  customLabel="Export KYP Report"
                />
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* 1. PLAYER PROFILE CARD - ALWAYS FIRST */}
      {playerProfile && (
        <PlayerProfileCard 
          playerProfile={playerProfile}
          playerData={null}
          playerInjuries={playerInjuries}
          playerTransfers={playerTransfers}
          playerAchievements={playerAchievements}
          playerStats={playerStats}
          isLoading={false}
          completedCategories={state.completedCategories}
          onScrollToAnalysis={scrollToAnalysis}
        />
      )}
      
      {/* 2. KYP RESULTS SECTION - Show when polling starts or during initial loading */}
      {(kypPipelineType === 'parallel_categories' || kypPipelineType === 'parallel_categories_mock') &&
       (state.isPolling || state.completedCategories.length > 0 || (phase === 'kyp_running' && !isComplete)) && (
        <div ref={kypResultsRef}>
          <KYPResultsSection
            completedCategories={state.completedCategories}
            categoryStatuses={state.categoryStatuses}
            isPolling={state.isPolling}
            selectedCategory={selectedCategoryFromAIS}
            onCategoryChange={setSelectedCategoryFromAIS}
          />
        </div>
      )}

      {/* 3. PLAYER SIMILARITY SECTION */}
      {playerProfile && selectedPlayer && (
        <EnhancedPlayerSimilaritySection 
          playerId={selectedPlayer.id}
          playerName={playerProfile.name}
        />
      )}
      
      {/* 4. KYP SEARCH RESULTS - Show legacy card only for non-parallel pipelines */}
      {isComplete && (
        <>
          {searchResults && kypPipelineType !== 'parallel_categories' && kypPipelineType !== 'parallel_categories_mock' ? (
            <PlayerAnalysisCard 
              playerData={searchResults}
              showHistoricalSearchDialog={false}
            />
          ) : !searchResults && kypPipelineType !== 'parallel_categories' && kypPipelineType !== 'parallel_categories_mock' ? (
            <div className="bg-[#3f1a1a] rounded-lg p-4">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <span className="font-medium text-[#CECFD2]">
                  {error || 'No search results available. The search completed but returned no data.'}
                </span>
              </div>
              <div className="mt-2">
                <Button
                  onClick={clearSearch}
                  variant="outline"
                  className="border-transparent bg-transparent hover:bg-[#26222E] text-[#CECFD2]"
                >
                  Start New Search
                </Button>
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};
